/**
 * ZeinShop, comportements du tiroir panier. Swiss K. Strategy.
 *
 * Deux rôles :
 * 1. les confettis au franchissement du seuil de livraison offerte ;
 * 2. l'ajout au panier depuis le bandeau de recommandations, sans quitter
 *    le tiroir.
 *
 * Le module est chargé une seule fois. Il ne peut pas vivre dans un
 * `<script>` du snippet : le tiroir est re-rendu par morphing à chaque
 * changement de panier, et un script déjà présent dans le DOM n'est pas
 * ré-exécuté.
 */

import { CartAddEvent } from '@theme/events';

const CONFETTI_COLORS = ['#6b2e3c', '#b07b45', '#521f2b', '#7a4e22', '#1a7f5a'];
const CONFETTI_COUNT = 44;

/** @returns {boolean} le tiroir est-il ouvert */
function drawerIsOpen() {
  const dialog = document.querySelector('.cart-drawer__dialog');
  return dialog instanceof HTMLDialogElement && dialog.open;
}

/** @returns {boolean} le seuil est-il franchi dans le DOM courant */
function thresholdReached() {
  return document.querySelector('[data-zein-fsb][data-reached="true"]') != null;
}

function burstConfetti() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const wrap = document.createElement('div');
  wrap.className = 'zein-confetti';
  wrap.setAttribute('aria-hidden', 'true');

  for (let i = 0; i < CONFETTI_COUNT; i++) {
    const piece = document.createElement('span');
    const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
    const wide = i % 3 === 0;

    piece.style.left = `${Math.random() * 100}vw`;
    piece.style.width = wide ? '9px' : '6px';
    piece.style.height = wide ? '6px' : '11px';
    piece.style.background = color;
    piece.style.animationDuration = `${2 + Math.random() * 1.6}s`;
    piece.style.animationDelay = `${Math.random() * 0.3}s`;
    wrap.appendChild(piece);
  }

  document.body.appendChild(wrap);
  window.setTimeout(() => wrap.remove(), 4400);
}

let wasReached = thresholdReached();
let checkQueued = false;

function checkThreshold() {
  if (checkQueued) return;
  checkQueued = true;

  // setTimeout et non requestAnimationFrame : rAF ne s'exécute pas dans un
  // onglet caché, la synchronisation de l'état resterait bloquée jusqu'au
  // retour du visiteur sur l'onglet.
  window.setTimeout(() => {
    checkQueued = false;
    const reached = thresholdReached();
    if (reached && !wasReached && drawerIsOpen()) burstConfetti();
    wasReached = reached;
  }, 0);
}

new MutationObserver(checkThreshold).observe(document.body, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['data-reached'],
});

document.addEventListener('cart:update', checkThreshold);

/**
 * Ajout au panier depuis le bandeau de recommandations.
 * Seules les références à variante unique portent ce bouton : on n'ajoute
 * jamais une taille à la place du client.
 */
document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-zein-reco-add]') : null;
  if (!(target instanceof HTMLButtonElement)) return;

  const variantId = target.dataset.zeinRecoAdd;
  if (!variantId) return;

  event.preventDefault();
  target.setAttribute('aria-busy', 'true');

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((node) => (node instanceof HTMLElement ? node.dataset.sectionId : null))
    .filter(Boolean);

  try {
    const addResponse = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    if (!addResponse.ok) throw new Error(`cart/add a répondu ${addResponse.status}`);
    const added = await addResponse.json();

    const cart = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    }).then((response) => response.json());

    document.dispatchEvent(
      new CartAddEvent(cart, 'zein-cart-crosssell', {
        source: 'zein-cart-crosssell',
        itemCount: cart.item_count,
        variantId,
        sections: added.sections,
      })
    );
  } catch (error) {
    console.error('[zein-cart] ajout impossible', error);
  } finally {
    target.removeAttribute('aria-busy');
  }
});
